
































































import { ConfigModule } from "@/store/modules/config";
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import Debug from "../Debug.vue";
import MImageMarkerMagnifier from "./MImageMarkerMagnifier.vue";
import MImageMarkerMarker from "./MImageMarkerMarker.vue";
import { IMarker } from "@/lib/interfaces/marker.interface";

type VImage = {
  image: HTMLImageElement;
  getBoundingClientRect: Function;
};

@Component({
  components: { Debug, MImageMarkerMagnifier, MImageMarkerMarker }
})
export default class MImageMarkerCanvas extends DarkModeHighlightMixin {
  @Prop() src!: string;
  @Prop({ default: "medium" }) markerSize!: "x-small" | "small" | "medium" | "large";
  @Prop() markers!: IMarker[];
  @Prop({ default: false }) deactivated!: boolean;
  @Prop({ default: false }) readonly!: boolean;
  @Prop({ default: 300 }) maxHeight!: number;
  @Prop({ required: true }) containerWidth!: number;

  imageLoaded = false;
  magnificationFactor = 4;
  magnifierTimeout: number | null = null;

  naturalWidth = 0;
  naturalHeight = 0;
  imageScaleFactor = 1;
  imageScaledDimensions: { width: number; height: number } = { width: 0, height: 0 };
  imageElement: HTMLElement | null = null;

  markerImage = this.generateComponentUid();

  get isDebug() {
    return ConfigModule.debug;
  }

  get imageClass() {
    let c = this.deactivated ? "image-deactivated" : "image-activated";
    c += this.isDebug ? " grey darken-4" : "";
    return c;
  }

  get image(): VImage | null {
    return (this.$refs[this.markerImage] as unknown) as VImage | null;
  }

  get magnifierRef(): null | MImageMarkerMagnifier {
    return (this.$refs?.magnifier as MImageMarkerMagnifier) || null;
  }

  beforeDestroy() {
    if (this.magnifierTimeout) {
      clearTimeout(this.magnifierTimeout);
    }
  }

  @Watch("containerWidth")
  onContainerWidthChange() {
    this.updateDimensions();
  }

  @Watch("src")
  onSrcChange() {
    this.imageLoaded = false;
    this.naturalWidth = 0;
    this.naturalHeight = 0;
    this.updateDimensions();
  }

  @Watch("maxHeight")
  onMaxHeightChange() {
    this.updateDimensions();
  }

  updateDimensions() {
    const naturalWidth = this.naturalWidth;
    const naturalHeight = this.naturalHeight;
    const availableWidth = this.containerWidth || 0;
    const maxHeightPx = this.maxHeight || 0;

    const vimgRef = this.$refs[this.markerImage] as Vue | undefined;
    if (vimgRef?.$el) {
      this.imageElement = vimgRef.$el as HTMLElement;
    }

    if (!naturalWidth || !naturalHeight || !availableWidth || !maxHeightPx) {
      this.imageScaleFactor = 1;
      this.imageScaledDimensions = { width: availableWidth || 0, height: 0 };
      return;
    }

    const scale = Math.min(availableWidth / naturalWidth, maxHeightPx / naturalHeight);
    this.imageScaleFactor = scale > 0 ? scale : 1;
    const width = Math.round(naturalWidth * this.imageScaleFactor);
    const height = Math.round(naturalHeight * this.imageScaleFactor);
    this.imageScaledDimensions = { width, height };
  }

  handleMarkerMouseOver(marker: IMarker) {
    this.$emit("mouseoverMarker", marker);
  }

  handleMarkerHover(marker: IMarker) {
    this.$emit("markerHover", marker);
  }

  handleMarkerMouseLeave(marker: IMarker) {
    this.$emit("mouseleaveMarker", marker);
  }

  addMarker(event: MouseEvent | TouchEvent) {
    if (this.deactivated) return;
    if (!this.imageLoaded) return;

    const rect = this.imageElement?.getBoundingClientRect();
    if (!rect) return;

    let mouseXPos = 0;
    let mouseYPos = 0;

    if (event instanceof MouseEvent) {
      mouseXPos = event.clientX - rect.left;
      mouseYPos = event.clientY - rect.top;
    } else if (event instanceof TouchEvent && event.changedTouches.length > 0) {
      const t = event.changedTouches[0];
      mouseXPos = t.clientX - rect.left;
      mouseYPos = t.clientY - rect.top;
    }

    const top = mouseYPos / rect.height;
    const left = mouseXPos / rect.width;

    if (top > 1 || left > 1 || top < 0 || left < 0) return;

    this.markerCreated({ top, left });
  }

  generateComponentUid(): string {
    const template = [1e7, -1e3, -4e3, -8e3, -1e11].join("");
    const replaceFunction = (c: string): string => {
      const randomValue = crypto.getRandomValues(new Uint8Array(1))[0];
      const newValue = (parseInt(c) ^ (randomValue & (15 >> (parseInt(c) / 4)))).toString(16);
      return newValue;
    };
    return template.replace(/[018]/g, replaceFunction);
  }

  onImageLoad(obj: any) {
    this.imageLoaded = true;
    const target = obj?.target as HTMLImageElement | undefined;
    this.naturalWidth = target?.naturalWidth || (this.image?.image?.naturalWidth as number) || 0;
    this.naturalHeight = target?.naturalHeight || (this.image?.image?.naturalHeight as number) || 0;
    this.$nextTick(() => {
      this.updateDimensions();
      const overlay = this.$refs?.imageOverlay as HTMLElement | undefined;
      if (overlay) {
        this.imageElement = overlay;
      }
    });
    this.$emit("imageLoaded");
  }

  onMouseDown(event: MouseEvent) {
    this.handlePointerStart(event);
  }

  onTouchStart(event: TouchEvent) {
    if (this.deactivated) return;
    event.preventDefault();
    this.handlePointerStart(event);
  }

  handlePointerStart(event: MouseEvent | TouchEvent) {
    if (this.deactivated) return;
    this.magnifierTimeout = window.setTimeout(() => {
      this.magnifierRef?.show();
      this.magnifierRef?.updateMagnifierPosition(event, this.imageElement);
    }, 500);
  }

  onMouseUp(event: MouseEvent) {
    this.handlePointerEnd();
    this.addMarker(event);
  }

  onTouchEnd(event: TouchEvent) {
    if (this.deactivated) return;
    event.preventDefault();
    this.handlePointerEnd();
    this.addMarker(event);
  }

  handlePointerEnd() {
    if (this.magnifierTimeout) {
      clearTimeout(this.magnifierTimeout);
    }
    this.magnifierRef?.hide();
  }

  onMouseMove(event: MouseEvent) {
    this.magnifierRef?.updateMagnifierPosition(event, this.imageElement);
  }

  onTouchMove(event: TouchEvent) {
    if (this.deactivated) return;
    event.preventDefault();
    this.magnifierRef?.updateMagnifierPosition(event, this.imageElement);
  }

  @Emit("markerCreated")
  markerCreated(marker: IMarker | undefined) {
    return marker;
  }
}
